// Typed client for the IP Pharmacy counter (routes/ip-pharmacy.js) — an
// attender or staff member buying medicine during an active admission. See
// that route file's own header for why this is a third front door onto the
// same pharmacy sale engine, distinct from both the walk-in counter and
// visit-linked Dispensing.
import { api } from '@/lib/api/client';
import type { PaymentMode } from '../types';

export interface AdmissionSearchResult {
  admission_id: number;
  patient_id: number;
  patient_name: string;
  uhid: string;
  mobile: string | null;
  deceased: boolean;
  bed_number: string;
  ward_name: string;
}

export interface AdmissionActiveOrder {
  id: number;
  medicine_id: number | null;
  drug_name: string;
  strength: string | null;
  status: 'active' | 'held';
}

export interface IpPharmacySaleLineInput {
  inventory_id: number;
  name?: string;
  quantity: number;
  gst_percent?: number;
  discount_percent?: number;
  batch_selection_reason?: string;
  batch_selection_remarks?: string;
}

export interface IpPharmacySaleInput {
  items: IpPharmacySaleLineInput[];
  prescriber_name?: string;
  prescriber_reg_no?: string;
  payment_mode: PaymentMode;
  /** Defaults 'Pending' server-side — settles at discharge unless paid now. */
  payment_status?: 'Paid' | 'Pending';
}

export interface OrderQuantityFlag {
  order_id: number;
  drug_name: string;
  implied_quantity: number | null;
  total_after: number;
  exceeds: boolean;
}

export interface IpPharmacySaleResult {
  bill: {
    id: number; bill_number: string; total_amount: string | number;
    payment_status: 'Paid' | 'Pending'; patient_id: number;
  };
  quantity_flags: OrderQuantityFlag[];
}

export function searchAdmissions(q: string): Promise<AdmissionSearchResult[]> {
  return api.get('/ip-pharmacy/admissions', { query: { q } });
}

export function fetchActiveOrders(admissionId: number): Promise<AdmissionActiveOrder[]> {
  return api.get(`/ip-pharmacy/admissions/${admissionId}/active-orders`);
}

export function createIpPharmacySale(admissionId: number, input: IpPharmacySaleInput): Promise<IpPharmacySaleResult> {
  return api.post(`/ip-pharmacy/admissions/${admissionId}/sales`, input);
}
